// SearchComponent.jsx
import React, { useState, useEffect, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { X } from 'lucide-react'; // Or use any clear/close icon you prefer
import './SearchComponent.css';

const SearchComponent = ({ onFundSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const searchFunds = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/funds/search?q=${encodeURIComponent(query)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      setSearchResults(data);
      setShowDropdown(true);
      setError(null);
    } catch (err) {
      setError('Failed to search funds');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearchFunds = useMemo(
    () => debounce(searchFunds, 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSearchFunds.cancel();
    };
  }, [debouncedSearchFunds]);

  const handleFundSelect = (fund) => {
    setSearchTerm(fund.name);
    setShowDropdown(false);
    onFundSelect(fund);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setShowDropdown(false);
    onFundSelect(null); // Notify parent component that search was cleared
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.search-input-container')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="search-input-container">
      <div className="search-input-wrapper">
        <input
          type="text"
          placeholder="Search funds..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            debouncedSearchFunds(e.target.value);
          }}
          onFocus={() => {
            if (searchResults.length > 0) setShowDropdown(true);
          }}
          className="search-input"
        />
        {searchTerm && (
          <button
            className="clear-button"
            onClick={handleClearSearch}
            type="button"
            aria-label="Clear search"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {loading && (
        <div className="search-loading">
          Searching...
        </div>
      )}

      {error && (
        <div className="search-error">
          {error}
        </div>
      )}

      {showDropdown && searchResults.length > 0 && (
        <div className="search-results-dropdown">
          {searchResults.map((fund) => (
            <div
              key={fund.id}
              className="search-result-item"
              onClick={() => handleFundSelect(fund)}
            >
              <div className="fund-name">{fund.name}</div>
              <div className="fund-symbol">{fund.symbol}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchComponent;
